import { trackAnalyticsAction } from "@/utils/analyticsBeacon";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { clearIndexCaches, clearSavedFilters, getTagPreferences, resetCacheSession, setTagPreferences } from "@/utils/localStore";
import { createSavedItem as apiCreateSaved, deleteSavedItem as apiDeleteSaved } from "@/utils/savedItemsApi";
import { AUTH_CONSENT_KEY, AUTH_TOKEN_KEY, authConsent, clearAuthToken, loadAuthToken, persistAuthToken } from "@/utils/authStorage";
import { API_BASE_URL } from "@/src/services/api";

/** Active organization membership as returned inside /auth/me. */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The user's access level in the given organization (matched by backend id
 * or slug), or null when they're not an active member. Site admins are NOT
 * special-cased here — check user.role === "admin" separately.
 */
export function getOrgAccess(user, org) {
  if (!user?.memberships?.length) return null;
  const match = user.memberships.find(m => org.id && m.orgId === org.id || org.slug && m.orgSlug === org.slug);
  return match?.accessLevel ?? null;
}

/** owner > manager > associate. */
export function orgAccessAtLeast(actual, required) {
  if (!actual) return false;
  const rank = {
    owner: 3,
    manager: 2,
    associate: 1
  };
  return rank[actual] >= rank[required];
}

/** Trust the role returned by the backend — no client-side allowlist. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeAuthUser = input => {
  const role = input.role === "admin" ? "admin" : "user";
  return {
    ...input,
    role
  };
};

/**
 * PATCH /auth/profile responses are bare user records — the backend only
 * enriches /auth/me and the login responses with memberships/savedItems.
 * Merge those responses over the previous user instead of replacing it,
 * or org access (owner/manager UI) silently vanishes after a profile edit.
 */
const mergeUserUpdate = (prev, next) => prev ? {
  ...next,
  memberships: next.memberships ?? prev.memberships,
  savedItems: next.savedItems ?? prev.savedItems
} : next;
const AuthContext = /*#__PURE__*/createContext(undefined);
const isDevMode = process.env.NODE_ENV !== "production";
const evaluateProfileCompleteness = profile => {
  if (!profile?.profile) return false;
  const hasName = Boolean(profile.profile.firstName && profile.profile.lastName);
  // Treat location as optional so existing users with name data aren't blocked
  return hasName;
};
const LOCAL_DEV_ACCOUNTS = isDevMode ? [{
  email: "cassano@productcertification.com",
  passwords: ["ProdCert.", "ProdCert"],
  token: "local:dave-admin",
  user: {
    id: "local-dave",
    email: "cassano@productcertification.com",
    displayName: "Dave Admin",
    role: "admin",
    preferences: {},
    metadata: {},
    cookieConsent: true
  }
}, {
  email: "nathankjoscode@gmail.com",
  passwords: ["ProdCert.", "ProdCert"],
  token: "local:nathan-editor",
  user: {
    id: "local-nathan",
    email: "nathankjoscode@gmail.com",
    displayName: "Nathan Editor",
    role: "admin",
    preferences: {},
    metadata: {},
    cookieConsent: true
  }
}, {
  email: "nathankjos@gmail.com",
  passwords: ["ProdCert.", "ProdCert"],
  token: "local:nathan-primary",
  user: {
    id: "local-nathan-gmail",
    email: "nathankjos@gmail.com",
    displayName: "Nathan Gmail",
    role: "admin",
    preferences: {},
    metadata: {},
    cookieConsent: true
  }
}, {
  email: "date@productcertification.com",
  passwords: ["ProdCert.", "ProdCert"],
  token: "local:date-admin",
  user: {
    id: "local-date",
    email: "date@productcertification.com",
    displayName: "Date Admin",
    role: "admin",
    preferences: {},
    metadata: {},
    cookieConsent: true
  }
}] : [];
const findLocalAccountByCredentials = (email, password) => LOCAL_DEV_ACCOUNTS.find(account => account.passwords.some(candidate => candidate === password) && account.email.toLowerCase() === email.toLowerCase());
const findLocalAccountByToken = token => LOCAL_DEV_ACCOUNTS.find(account => account.token === token);
async function parseErrorMessage(response) {
  try {
    const data = await response.json();
    if (data?.message) {
      if (Array.isArray(data.message)) {
        return data.message.join(", ");
      }
      return String(data.message);
    }
  } catch {
    // Ignore JSON parse errors and fall back to status text
  }
  const text = response.statusText || "Request failed";
  return text;
}
function ensureApiBase() {
  if (!API_BASE_URL) {
    throw new Error("Auth API base URL is not configured. Set NEXT_PUBLIC_API_BASE_URL in your environment.");
  }
}
export const AuthProvider = ({
  children
}) => {
  const router = useRouter();
  const lastRouteRef = useRef(null);
  const [user, setUser] = useState(null);

  // Hydrate followed tags from the server profile: the durable copy lives
  // in user.metadata.followedTags (saved by the profile modal), so a wiped
  // localStorage (Refresh Data, new browser) re-seeds automatically and
  // previously-followed tags come back toggled on by default.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const server = user?.metadata?.followedTags;
    if (!server) return;
    const clean = arr => Array.isArray(arr) ? arr.filter(v => typeof v === "string" && v.trim()) : [];
    const serverPrefs = {
      regions: clean(server.regions),
      disciplines: clean(server.disciplines),
      products: clean(server.products)
    };
    const serverHasAny = serverPrefs.regions.length || serverPrefs.disciplines.length || serverPrefs.products.length;
    if (!serverHasAny) return;
    const local = getTagPreferences();
    const localHasAny = local.regions.length || local.disciplines.length || local.products.length;
    // Seed only when local is empty — never clobber fresh local toggles.
    if (!localHasAny) setTagPreferences(serverPrefs);
  }, [user]);
  const [token, setToken] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileStatus, setProfileStatus] = useState("unknown");
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profilePrompted, setProfilePrompted] = useState(false);
  const [profileUpdating, setProfileUpdating] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [savedItems, setSavedItems] = useState([]);
  const fetchCurrentUser = useCallback(async accessToken => {
    const localAccount = findLocalAccountByToken(accessToken);
    if (localAccount) {
      return normalizeAuthUser(localAccount.user);
    }
    ensureApiBase();
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    const raw = await response.json();
    const normalized = normalizeAuthUser(raw);
    // Extract savedItems returned by /auth/me
    if (Array.isArray(raw.savedItems)) {
      setSavedItems(raw.savedItems);
    }
    return normalized;
  }, []);
  const syncFromStorage = useCallback(async () => {
    if (typeof window === "undefined") return;
    // The session token is governed by its own consent (set at login), not
    // by the general cache banner — so declining that banner must NOT sign
    // the user out here. loadAuthToken() returns the token whenever one was
    // persisted; absence of a token is the only "logged out" signal.
    const storedToken = loadAuthToken();
    if (!storedToken) {
      setToken(null);
      setUser(null);
      return;
    }
    try {
      const profile = await fetchCurrentUser(storedToken);
      setToken(storedToken);
      setUser(profile);
    } catch (error) {
      clearAuthToken();
      setToken(null);
      setUser(null);
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[Auth] Failed to restore session", error);
      }
    }
  }, [fetchCurrentUser]);
  useEffect(() => {
    if (typeof window === "undefined") {
      setInitializing(false);
      return;
    }
    let cancelled = false;
    const boot = async () => {
      try {
        await syncFromStorage();
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };
    void boot();

    // Re-sync only on session-relevant changes: the token itself, the
    // auth-consent key, or a full clear. The general cache banner
    // (`pc_cookie_prefs`) is deliberately NOT a trigger — toggling it must
    // never touch the session.
    const handleStorage = event => {
      if (event.key === null || event.key === AUTH_TOKEN_KEY || event.key === AUTH_CONSENT_KEY) {
        void syncFromStorage();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", handleStorage);
    };
  }, [syncFromStorage]);
  const applySession = useCallback((accessToken, authUser) => {
    const normalizedUser = normalizeAuthUser(authUser);
    setToken(accessToken);
    setUser(normalizedUser);

    // New login session — invalidate stale index caches
    clearIndexCaches();
    resetCacheSession();

    // Completing login is consent to keep the session token — grant it
    // before persisting so the token actually writes.
    authConsent.grant();
    const persisted = persistAuthToken(accessToken);
    if (!persisted && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[Auth] Unable to persist auth token. Session will reset on refresh.");
    }
  }, [setToken, setUser]);
  const requestMagicLink = useCallback(async email => {
    ensureApiBase();
    const response = await fetch(`${API_BASE_URL}/auth/request-link`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email
      })
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Auth endpoint not found. Check NEXT_PUBLIC_API_BASE_URL points to the backend API (e.g. http://localhost:3001).");
      }
      throw new Error(await parseErrorMessage(response));
    }
    const data = await response.json();
    return {
      existingUser: Boolean(data.existingUser),
      email: String(data.email),
      expiresAt: String(data.expiresAt),
      admin: Boolean(data.admin),
      // Present only when the backend has dev auth enabled and the
      // mail transport is unavailable — lets dev logins complete
      // without SMTP.
      ...(data.previewToken ? {
        previewToken: String(data.previewToken)
      } : {})
    };
  }, []);
  const verifyMagicLink = useCallback(async ({
    token: magicToken,
    cookieConsentAccepted
  }) => {
    ensureApiBase();
    const response = await fetch(`${API_BASE_URL}/auth/verify-link`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        token: magicToken,
        cookieConsentAccepted
      })
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Auth endpoint not found. Check NEXT_PUBLIC_API_BASE_URL points to the backend API (e.g. http://localhost:3001).");
      }
      throw new Error(await parseErrorMessage(response));
    }
    const data = await response.json();
    const normalizedUser = normalizeAuthUser(data.user);
    setToken(data.accessToken);
    setUser(normalizedUser);

    // The magic link was verified with cookieConsentAccepted:true, so
    // grant session-token consent before persisting.
    authConsent.grant();
    const persisted = persistAuthToken(data.accessToken);
    if (!persisted) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[Auth] Unable to persist auth token. Session will reset on refresh.");
      }
    }
    try {
      localStorage.removeItem("pc_newsletter_confirmed");
    } catch {
      /* SSR */
    }
    return normalizedUser;
  }, []);
  const loginWithPassword = useCallback(async ({
    email,
    password,
    cookieConsentAccepted
  }) => {
    const runLocalFallback = () => {
      const account = findLocalAccountByCredentials(email, password);
      if (!account) {
        throw new Error("Invalid credentials");
      }
      const normalized = normalizeAuthUser(account.user);
      applySession(account.token, normalized);
      return normalized;
    };
    if (!API_BASE_URL) {
      if (!isDevMode) {
        throw new Error("Authentication service is not configured.");
      }
      return runLocalFallback();
    }
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          password,
          cookieConsentAccepted
        })
      });
      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }
      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error("Received malformed response from auth service");
      }
      applySession(data.accessToken, data.user);
      try {
        localStorage.removeItem("pc_newsletter_confirmed");
      } catch {
        /* SSR */
      }
      return normalizeAuthUser(data.user);
    } catch (error) {
      if (isDevMode) {
        try {
          return runLocalFallback();
        } catch {
          // Fall through to throw original error
        }
      }
      throw error instanceof Error ? error : new Error("Login failed");
    }
  }, [applySession]);
  const completeProfile = useCallback(async profile => {
    const activeToken = token ?? loadAuthToken();
    if (!activeToken) {
      throw new Error("You must be signed in to update your profile.");
    }
    ensureApiBase();
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${activeToken}`
      },
      body: JSON.stringify(profile)
    });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    const data = await response.json();
    const normalized = normalizeAuthUser(data);
    setUser(prev => mergeUserUpdate(prev, normalized));
    setProfile(normalized);
    const isComplete = evaluateProfileCompleteness(normalized);
    setProfileStatus(isComplete ? "complete" : "incomplete");
    setProfilePrompted(isComplete);
    setProfileModalOpen(!isComplete);
    setToken(activeToken);
    return normalized;
  }, [token]);
  const completeProfileByEmail = useCallback(async (email, profile) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      throw new Error("We couldn't determine your email. Please request a new magic link.");
    }
    const activeToken = token ?? loadAuthToken();
    if (!activeToken) {
      throw new Error("You must be signed in to update your profile.");
    }
    ensureApiBase();
    const response = await fetch(`${API_BASE_URL}/auth/profile/by-email`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${activeToken}`
      },
      body: JSON.stringify({
        email: normalizedEmail,
        firstName: profile.firstName,
        lastName: profile.lastName,
        locationCity: profile.locationCity,
        locationState: profile.locationState,
        locationCountry: profile.locationCountry
      })
    });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    const data = await response.json();
    const normalized = normalizeAuthUser(data);
    setUser(prev => mergeUserUpdate(prev, normalized));
    setProfile(normalized);
    const isComplete = evaluateProfileCompleteness(normalized);
    setProfileStatus(isComplete ? "complete" : "incomplete");
    setProfilePrompted(isComplete);
    setProfileModalOpen(!isComplete);
    setToken(activeToken);
    return normalized;
  }, [token]);
  const logout = useCallback(() => {
    clearAuthToken();
    clearIndexCaches();
    resetCacheSession();
    clearSavedFilters();
    try {
      localStorage.removeItem("pc_newsletter_confirmed");
    } catch {
      /* SSR */
    }
    setToken(null);
    setUser(null);
    setProfile(null);
    setProfileStatus("unknown");
    setProfileModalOpen(false);
    setProfilePrompted(false);
    setProfileError(null);
    setSavedItems([]);
  }, []);
  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const next = await fetchCurrentUser(token);
      setUser(next);
      return next;
    } catch (error) {
      clearAuthToken();
      setToken(null);
      setUser(null);
      throw error;
    }
  }, [fetchCurrentUser, token]);
  const loadProfile = useCallback(async () => {
    // If we already have a complete profile in memory, don't overwrite it
    const existingProfile = user ?? profile;
    if (evaluateProfileCompleteness(existingProfile)) {
      setProfile(existingProfile);
      setProfileStatus("complete");
      setProfilePrompted(true);
      return;
    }
    const authToken = token ?? loadAuthToken();
    if (!authToken) {
      setProfile(null);
      setProfileStatus("unknown");
      setProfilePrompted(false);
      return;
    }
    try {
      const localAccount = findLocalAccountByToken(authToken);
      let data;
      if (localAccount) {
        data = localAccount.user;
      } else {
        ensureApiBase();
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
          }
        });
        if (!response.ok) throw new Error("Unable to load profile");
        data = await response.json();
      }
      const normalizedProfile = normalizeAuthUser(data);
      // Preserve existing name data if the /auth/me response omits profile
      if (!normalizedProfile.profile && existingProfile?.profile) {
        normalizedProfile.profile = existingProfile.profile;
      }
      setProfile(normalizedProfile);
      const complete = evaluateProfileCompleteness(normalizedProfile);
      setProfileStatus(complete ? "complete" : "incomplete");
      setProfilePrompted(complete);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Failed to load profile", error);
      }
      setProfileStatus("unknown");
    }
  }, [token, user, profile]);
  const updateProfile = useCallback(async input => {
    setProfileUpdating(true);
    setProfileError(null);
    try {
      const authToken = token ?? loadAuthToken();
      if (!authToken) {
        throw new Error("You must be signed in to update your profile.");
      }
      ensureApiBase();
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          firstName: input.firstName,
          lastName: input.lastName,
          followedTags: input.followedTags,
          locationCity: input.city,
          locationState: input.state,
          locationCountry: input.country,
          timezone: input.timezone
        })
      });
      if (!response.ok) {
        const message = await parseErrorMessage(response);
        throw new Error(message || "Unable to update profile");
      }
      const data = await response.json();
      const normalizedProfile = normalizeAuthUser(data);
      setProfile(normalizedProfile);
      setUser(prev => mergeUserUpdate(prev, normalizedProfile));
      const complete = evaluateProfileCompleteness(normalizedProfile);
      setProfileStatus(complete ? "complete" : "incomplete");
      setProfilePrompted(complete);
      setProfileModalOpen(!complete);
      return normalizedProfile;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to update profile";
      setProfileError(message);
      throw error;
    } finally {
      setProfileUpdating(false);
    }
  }, [token]);
  const addSavedItem = useCallback(async payload => {
    if (!token) return;
    // Optimistic local update
    const placeholder = {
      id: `pending-${payload.resourceId}`,
      userId: user?.id ?? "",
      resourceType: payload.resourceType,
      resourceId: payload.resourceId,
      resourceSlug: payload.resourceSlug ?? null,
      resourceTitle: payload.resourceTitle ?? null,
      resourceSummary: payload.resourceSummary ?? null,
      resourceImage: payload.resourceImage ?? null,
      meta: payload.meta ?? null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setSavedItems(prev => [...prev, placeholder]);
    try {
      const created = await apiCreateSaved(token, payload);
      // Replace placeholder with real item
      setSavedItems(prev => prev.map(item => item.id === placeholder.id ? created : item));
      // Only count saves the backend actually accepted — a
      // rolled-back optimistic save must not inflate the KPI.
      trackAnalyticsAction("save_item");
    } catch {
      // Rollback
      setSavedItems(prev => prev.filter(item => item.id !== placeholder.id));
    }
  }, [token, user?.id]);
  const removeSavedItem = useCallback(async (resourceType, resourceId) => {
    if (!token) return;
    const normalizedId = resourceId.trim().toLowerCase();
    // Optimistic removal
    let removed = [];
    setSavedItems(prev => {
      removed = prev.filter(item => item.resourceType === resourceType && item.resourceId === normalizedId);
      return prev.filter(item => !(item.resourceType === resourceType && item.resourceId === normalizedId));
    });
    try {
      await apiDeleteSaved(token, resourceType, normalizedId);
    } catch {
      // Rollback
      setSavedItems(prev => [...prev, ...removed]);
    }
  }, [token]);
  const openProfileModal = useCallback(() => {
    setProfileModalOpen(true);
    setProfilePrompted(false);
  }, []);
  const closeProfileModal = useCallback(() => {
    setProfileModalOpen(false);
  }, []);

  // Load profile when authenticated
  useEffect(() => {
    if (!token) return;
    void loadProfile();
  }, [token, loadProfile]);

  // Show profile modal if incomplete and not already prompted
  useEffect(() => {
    if (!user || profileStatus !== "incomplete") return;
    if (!profileModalOpen && !profilePrompted) {
      setProfileModalOpen(true);
    }
  }, [user, profileStatus, profileModalOpen, profilePrompted]);

  // Show profile modal on route changes when incomplete
  useEffect(() => {
    if (!router.isReady) return;
    if (!user || profileStatus !== "incomplete") {
      lastRouteRef.current = router.asPath;
      return;
    }
    if (lastRouteRef.current && lastRouteRef.current !== router.asPath) {
      setProfileModalOpen(true);
    }
    lastRouteRef.current = router.asPath;
  }, [router.isReady, router.asPath, user, profileStatus]);
  useEffect(() => {
    const {
      isReady,
      pathname,
      asPath
    } = router;
    if (!isReady) return;
    if (!user) return;
    if (profileStatus !== "incomplete") return;
    if (pathname === "/login") return;
    const currentPath = asPath.startsWith("/") ? asPath : "/";
    void router.replace(currentPath !== "/login" ? {
      pathname: "/login",
      query: currentPath && currentPath !== "/login" ? {
        returnUrl: currentPath
      } : undefined
    } : "/login");
  }, [router.isReady, router.pathname, router.asPath, router.replace, user, profileStatus]);
  const value = useMemo(() => ({
    user,
    token,
    initializing,
    isAuthenticated: Boolean(user),
    requestMagicLink,
    verifyMagicLink,
    loginWithPassword,
    logout,
    refreshUser,
    completeProfile,
    completeProfileByEmail,
    profile,
    profileStatus,
    profileModalOpen,
    profileUpdating,
    profileError,
    openProfileModal,
    closeProfileModal,
    refreshProfile: loadProfile,
    updateProfile,
    savedItems,
    addSavedItem,
    removeSavedItem
  }), [user, token, initializing, requestMagicLink, verifyMagicLink, loginWithPassword, logout, refreshUser, completeProfile, completeProfileByEmail, profile, profileStatus, profileModalOpen, profileUpdating, profileError, openProfileModal, closeProfileModal, loadProfile, updateProfile, savedItems, addSavedItem, removeSavedItem]);
  return /*#__PURE__*/_jsx(AuthContext.Provider, {
    value: value,
    children: children
  });
};
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}